import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import {
  withRouter,
  RouteComponentProps,
  generatePath,
} from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

import AddIcon from "@material-ui/icons/Add";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";

import { Button } from "@material-ui/core";

import ActiveProgramDictionary from "stores/dictionary/ActiveProgramDictionary";
import InAppResourcesStore from "stores/InAppResourcesStore";
import ResourcesCategoryDetailsStore from "stores/ResourcesCategoryDetailsStore";

import Container from "components/Layout/Container";
import Header from "components/Layout/Header";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import Page from "components/Layout/Page";
import ConfirmationDialog from "components/ConfirmationDialog";
import Placeholder from "components/Placeholder";
import PublishStatus from "components/PublishStatus";
import FilteringControl from "components/FilteringControl";
import PublishButton from "components/PublishButton";
import { SNACKBAR_TYPES } from "components/Snackbar";

import AccessForm from "./AccessForm";
import CategoryDetailsCard from "./CategoryDetailsCard";
import AddQuickLinksForm from "./AddQuickLinksForm";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { areStatusActiveProgramFiltersEmpty } from "utils/filter";
import { getConfirmRemovalText } from "utils/removeItem";

import { PROGRAM_PERMISSION_TYPES, PUBLISH_STATUSES } from "constants/models";
import { FILTER_COLUMNS } from "constants/filterConstants";

import ROUTES from "navigation/routes";

import {
  CategoryCardWrapper,
  BackButtonWrapper,
  SearchContainer,
  Search,
  SearchControls,
  SearchControlWrapper,
  ScrollLoader,
  OverflowLoader,
  PublishStatusWrapper,
} from "./styles";

type Props = GlobalPopupProps &
  SnackbarContextProps &
  RouteComponentProps<{ id: string }> & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class ResourcesCategoryDetails extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;

  get categoryId() {
    const { id } = this.props.match.params;

    return id ? Number.parseInt(id) : null;
  }

  @computed get category() {
    return InAppResourcesStore.categoryDetails;
  }

  @computed get categoryTitle() {
    return this.category?.title;
  }

  @computed get categoryPublishStatus() {
    return this.category?.publishStatus;
  }

  @computed get shouldShowUpdateConfirmation() {
    return (
      this.categoryPublishStatus &&
      [
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
        PUBLISH_STATUSES.PUBLISHED,
      ].includes(this.categoryPublishStatus)
    );
  }

  @computed get quickLinks() {
    return ResourcesCategoryDetailsStore.items;
  }

  @computed get quickLinkListIsEmpty() {
    return this.quickLinks.length === 0;
  }

  @computed get isFiltersEmpty() {
    const { filters } = ResourcesCategoryDetailsStore;

    return areStatusActiveProgramFiltersEmpty(filters);
  }

  componentDidMount() {
    if (this.categoryId) {
      ResourcesCategoryDetailsStore.applyFilter({
        column: "ResourceCategoryId",
        value: this.categoryId,
      });
      Promise.all([
        InAppResourcesStore.fetchCategoryDetails(this.categoryId),
        ResourcesCategoryDetailsStore.fetch(),
        ActiveProgramDictionary.fetch(),
      ]);
    }
  }

  componentWillUnmount() {
    ResourcesCategoryDetailsStore.reset();
  }

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  onBackClick = () => {
    this.props.history.push(ROUTES.IN_APP_RESOURCES);
  };

  fetchMoreQuickLinks = async () => {
    const { setMessage } = this.props;

    try {
      await ResourcesCategoryDetailsStore.fetchMoreQuicklinks();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    ResourcesCategoryDetailsStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH &&
      ResourcesCategoryDetailsStore.fetch();
  };

  onSearchClear = () => {
    ResourcesCategoryDetailsStore.clearSearch();
    ResourcesCategoryDetailsStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      ResourcesCategoryDetailsStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    ResourcesCategoryDetailsStore.fetch();
  };

  onFilterClear = () => {
    ResourcesCategoryDetailsStore.clearFilter(FILTER_COLUMNS.ACTIVE_PROGRAMS);
    ResourcesCategoryDetailsStore.clearFilter(FILTER_COLUMNS.PUBLISH_STATUS);
    ResourcesCategoryDetailsStore.fetch();
  };

  onAddQuickLinks = () => {
    const { openPopup } = this.props;

    openPopup({
      key: "AddQuickLinks",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      confirmation: {
        renderConfirmation: ({ deny, allow }) => (
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={deny}
            onAccept={allow}
          />
        ),
        shouldShowConfirmation: this.shouldShowConfirmation,
      },
      render: ({ close }) => (
        <AddQuickLinksForm
          categoryId={this.categoryId}
          isLoading={ResourcesCategoryDetailsStore.isDetailsManaging}
          onSubmit={this.onAddQuickLinksSubmit(close)}
          onClose={close}
          onChangeIsDirty={this.onChangeIsPopupDirty}
          shouldShowUpdateConfirmation={this.shouldShowUpdateConfirmation}
        />
      ),
    });
  };

  onAddQuickLinksSubmit = (close: () => void) => async (values: any) => {
    const { setMessage } = this.props;
    const { quickLinks } = values;

    const payload = {
      categoryId: this.categoryId,
      quickLinkIds: quickLinks?.map(({ id }: { id: number }) => id),
    };

    if (!payload.quickLinkIds || payload.quickLinkIds?.length === 0) {
      this.resetIsPopupDirty();
      close();
      return;
    }

    try {
      await ResourcesCategoryDetailsStore.addQuickLinkToCategory(payload);
      this.resetIsPopupDirty();
      close();
      ResourcesCategoryDetailsStore.resetPage();
      ResourcesCategoryDetailsStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onManageAccessPrograms = (quickLink: any) => () => {
    const { openPopup } = this.props;

    const shouldShowUpdateConfirmation =
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(quickLink.publishStatus) && this.shouldShowUpdateConfirmation;

    openPopup({
      key: "ManageAccessPrograms",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      confirmation: {
        renderConfirmation: ({ deny, allow }) => (
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={deny}
            onAccept={allow}
          />
        ),
        shouldShowConfirmation: this.shouldShowConfirmation,
      },
      render: ({ close }) => (
        <AccessForm
          isLoading={ResourcesCategoryDetailsStore.isDetailsManaging}
          isFormLoading={ResourcesCategoryDetailsStore.isQuickLinkLoading}
          initialValues={{
            categoryId: this.categoryId,
            quickLinkId: quickLink.id,
          }}
          onSubmit={this.onManageAccessProgramsSubmit(quickLink, close)}
          onClose={close}
          onChangeIsDirty={this.onChangeIsPopupDirty}
          shouldShowUpdateConfirmation={shouldShowUpdateConfirmation}
        />
      ),
    });
  };

  onManageAccessProgramsSubmit = (quickLink: any, close: () => void) => async (
    values: any,
  ) => {
    const { setMessage } = this.props;
    const { activePrograms } = values;

    const payload = {
      categoryId: this.categoryId,
      quickLinkId: quickLink.id,
      activePrograms: activePrograms.map(
        ({ id, switchState }: { id: number; switchState: boolean }) => ({
          activeProgramId: id,
          permission: switchState
            ? PROGRAM_PERMISSION_TYPES.PREVIEW
            : PROGRAM_PERMISSION_TYPES.FULL_ACCESS,
        }),
      ),
    };

    try {
      await ResourcesCategoryDetailsStore.updateQuickLinkAccessPrograms(
        payload,
      );
      this.resetIsPopupDirty();
      close();
      ResourcesCategoryDetailsStore.resetPage();
      ResourcesCategoryDetailsStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onRemoveQuickLink = (quickLink: any) => () => {
    const { openPopup } = this.props;

    const text = getConfirmRemovalText({
      entity: quickLink,
      suffix: "quick link",
      category: this.category,
    });

    openPopup({
      key: "RemoveQuickLink",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title="Remove Quick Link"
          text={text}
          declineText="Cancel"
          acceptText="Remove"
          onDecline={close}
          onAccept={this.onRemoveQuickLinkSubmit(close, quickLink)}
          isLoading={ResourcesCategoryDetailsStore.isDetailsManaging}
        />
      ),
    });
  };

  onRemoveQuickLinkSubmit = (close: () => void, quickLink: any) => async () => {
    const { setMessage } = this.props;

    const payload = {
      quickLinkIds: [quickLink?.id],
      categoryId: this.categoryId,
    };

    try {
      await ResourcesCategoryDetailsStore.removeQuickLinkFromCategory(payload);
      close();
      ResourcesCategoryDetailsStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onDetailsClick = (quickLink: any) => () => {
    const path = generatePath(
      ROUTES.IN_APP_RESOURCES_CATEGORY_DETAILS_QUICK_LINK_DETAILS,
      { id: this.categoryId, quickLinkId: quickLink.id },
    );
    this.props.history.push({ pathname: path, state: { readOnly: true } });
  };

  onDragEnd = async (result: DropResult) => {
    const { setMessage } = this.props;

    // dropped outside the list
    if (
      !result.destination ||
      !result.source ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    try {
      this.categoryId &&
        (await ResourcesCategoryDetailsStore.reorderQuickLinks({
          categoryId: this.categoryId,
          startIndex,
          endIndex,
        }));
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  shouldShowConfirmation = () => {
    return this.isPopupDirty;
  };

  render() {
    return (
      <Container header={<Header controls={<PublishButton />} />}>
        <Page
          topControls={
            <BackButtonWrapper>
              <Button
                color="secondary"
                variant="text"
                startIcon={<KeyboardBackspaceIcon />}
                onClick={this.onBackClick}
              >
                Back to categories
              </Button>
            </BackButtonWrapper>
          }
          title={this.categoryTitle}
          leftControls={
            this.categoryPublishStatus && (
              <PublishStatusWrapper>
                <PublishStatus status={this.categoryPublishStatus} />
              </PublishStatusWrapper>
            )
          }
          controls={
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={this.onAddQuickLinks}
            >
              Add Quick Link
            </Button>
          }
          isLoading={
            InAppResourcesStore.isCategoryLoading ||
            ResourcesCategoryDetailsStore.isLoading
          }
        >
          <SearchContainer>
            <Search
              initValue={ResourcesCategoryDetailsStore.search}
              onApply={this.onSearchApply}
              onClear={this.onSearchClear}
            />
            <SearchControls>
              <SearchControlWrapper>
                <FilteringControl
                  initValue={ResourcesCategoryDetailsStore.filters}
                  onApply={this.onFilterApply}
                  onClear={this.onFilterClear}
                  statusFiltering
                  accessProgramsFiltering
                />
              </SearchControlWrapper>
            </SearchControls>
          </SearchContainer>

          {!this.quickLinkListIsEmpty ? (
            <>
              {ResourcesCategoryDetailsStore.isQuickLinkReordering && (
                <OverflowLoader />
              )}
              <InfiniteScroll
                dataLength={this.quickLinks.length}
                hasMore={ResourcesCategoryDetailsStore.hasMoreInfo}
                next={this.fetchMoreQuickLinks}
                height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
                loader={<ScrollLoader />}
              >
                <DragDropContext onDragEnd={this.onDragEnd}>
                  <Droppable droppableId="droppable">
                    {(provided, snapshot) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {this.quickLinks.map((quickLink, index) => (
                          <Draggable
                            key={quickLink.id}
                            draggableId={quickLink.id.toString()}
                            index={index}
                            isDragDisabled={!this.isFiltersEmpty}
                          >
                            {(provided, snapshot) => (
                              <CategoryCardWrapper
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                              >
                                <CategoryDetailsCard
                                  title={quickLink.title}
                                  image={quickLink.imageUrl}
                                  publishStatus={quickLink.publishStatus}
                                  onDetailsClick={this.onDetailsClick(
                                    quickLink,
                                  )}
                                  onAccessClick={this.onManageAccessPrograms(
                                    quickLink,
                                  )}
                                  onRemove={this.onRemoveQuickLink(quickLink)}
                                  isDragging={snapshot.isDragging}
                                  dragHandleProps={provided.dragHandleProps}
                                />
                              </CategoryCardWrapper>
                            )}
                          </Draggable>
                        ))}

                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </InfiniteScroll>
            </>
          ) : (
            <Placeholder
              height={280}
              width={280}
              text={
                ResourcesCategoryDetailsStore.search
                  ? "No matches found. Please try another search."
                  : "No quick links added yet."
              }
            />
          )}
        </Page>
      </Container>
    );
  }
}

export default withGlobalPopup(
  withGlobalSnackbar(withRouter(ResourcesCategoryDetails)),
);
